// core
import { Component } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { NgbActiveModal, NgbModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';

// services
import { ToastService } from "../../services/toast.service";
import { LoginService } from "../../services/login.service";

// API
import {
    OperationClient,
    JobClient,
    OperationListOperation,
    Status,
    CreateOperationRequest,
    Type,
    DateTimeTba,
    TerminalOperation,
    Customer,
    TerminalJob,
    CreateJobRequest,
    ResourceClient,
    GetResourceRequest,
    GetResourceListResponse,
    OperationListResource,
    LocationClient,
    TerminalLocation,
    TerminalTask,
    CreateTaskRequest,
    TaskClient,
    UserRight,
    DepartmentClient,
    OperationCommentClient,
} from "../../WebApiClient";

// models & components
import { ComponentBase } from "../../models/ComponentBase";
import { TypedModalRef } from "../../models/TypedModalRef";
import { Guid } from "../../models/Guid";
import { CustomerService } from "../../services/customer.service";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs/operators";
import { ModeType } from "../../models/ModeTypes";

// assets
import { faPlusSquare } from "@fortawesome/free-regular-svg-icons";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";

export class ExtendedJob extends TerminalJob {
    public nonUniqName: boolean = false;
}
export class ExtendedTask extends TerminalTask {
    public name = '';
    public nonUniqName: boolean = false;
}
export class ExtendedWorker extends OperationListResource {
    public personNumber: string = "";
    public nonUniqName: boolean = false;
}
export class WorkersList {
    public groups: string[] = [];
    public workers: ExtendedWorker[] = [];
    public category: string = "";
}

// #region Component
@Component({
    selector: "app-operation-create-edit",
    standalone: false,
    templateUrl: "./operation-create-edit.component.html",
    styleUrls: ["./operation-create-edit.component.scss"],
    providers: [OperationClient, JobClient, TaskClient, ResourceClient, LocationClient, DepartmentClient, OperationCommentClient],
})
export class TerminalOperationCreateEditComponent extends ComponentBase {
    public operationForm: FormGroup;
    public isLoadingState = false;
    public isCreateMode = false;
    public isRequestMode = false;
    public isEditMode = false;
    public isCopyMode = false;
    public isAcceptMode = false;
    public isLongTermOperation = false;
    public isNewJobRequested = false;
    public isNewTaskRequested = false;

    public fetchedWorkers: ExtendedWorker[] = [];
    public responsible: WorkersList = new WorkersList();
    public available: WorkersList = new WorkersList();
    public currentWorkersList: WorkersList | undefined = undefined;

    private pendingPersons: string[] = [];
    public involvedPersons: ExtendedWorker[] = [];
    private sortInProgres: boolean = false;
    private sortPending = 0;    // amount of sorting requests formed after performing `removeInvolved` procedure


    public jobsFetched = true;
    public filteredJobs: ExtendedJob[] = [];
    public selectedJob: ExtendedJob | undefined = undefined;
    public fullJobsList: ExtendedJob[] = [];
    public searchByJobNumber = false;
    public defaultCustomerNumber = 9999;    // Projektoprettelses kunde - husk at ændre debitor

    public tasksFetched = true;
    public filteredTasks: ExtendedTask[] = [];
    public selectedTask: ExtendedTask | undefined = undefined;

    public isCustomerSearchFocused: boolean = false;
    public defineOperationEstimatedTime!: (input: OperationListOperation) => string;   // TODO [4ref] should be connected with DashboardComponent.getOperationEstimatedTime()
    public periodsInMinutes = [0, 15, 30, 45];
    readonly AddIcon = faPlusSquare;
    readonly faDelete = faTrashAlt;

    public locations: TerminalLocation[] = [];
    public otherLocationName = "... other";

    //refactor: move customer stuff into seperate component
    customers: Customer[] | undefined;
    selectedCustomer: Customer | undefined = undefined;

    private padNum = (n: number) => n.toString().padStart(2, "0");
    private readonly trigger = new BehaviorSubject(true);

    public operation: OperationListOperation | undefined = undefined;
    private workingMode: ModeType | undefined = undefined;
    private veryImportantFields = ["customer", "job", "task"]; // values of this fields must be submitted even if this control is disabled

    public now: Date= new Date();
    public timeShiftInMinutes: number = 15; // default time shift for startDateTime field
    public operationstartDateTimeBeforeUpdate: Date | undefined = undefined;
    public minDate: {year:number, month:number, day:number} | undefined = undefined;
    public calculatedStartDateTime: Date = this.now;
    public calculatedEndDateTime: Date = this.now;

    public taskNumberSeries: any[] = [];
    public departmentCodes: { label: string; value: string }[] = [];

    public showLogbook = false;
    public hasUnreadComments = false;

    // #region Construct
    public constructor(
        private readonly loginService: LoginService,
        private operationClient: OperationClient,
        private jobClient: JobClient,
        private taskClient: TaskClient,
        private fb: FormBuilder,
        private readonly toastService: ToastService,
        private customerService: CustomerService,
        private readonly activeModal: NgbActiveModal,
        private resourceClient: ResourceClient,
        private locationClient: LocationClient,
        private departmentClient: DepartmentClient,
        private operationCommentClient: OperationCommentClient,
    ) {
        super();
        this.responsible.category = "responsible";
        this.responsible.groups = [
            "FORMÆNDS",
            "FORMÆNDT",
            "FORMÆNDL",
            "STEVEDORE",
            "EKSTERN",
            "LINDØ",
            "MLEDER",
        ];
        this.available.category = "available";
        this.available.groups = [
            "FORMÆNDL",
            "LØSARBF",
            "LØSARBUF",
            "TERM.ARBF",
            "TERM.ARBUF",
            "LINDØ",
        ];
        this.minDate = {
            year: this.now.getFullYear() -1,
            month: this.now.getMonth() + 1,
            day: this.now.getDate()
        };
        this.operationForm = this.fb.group({
            customer: ['', Validators.required],
            job: [{value: '', disabled: true}, Validators.required],
            job_name: [{ value: '', disabled: true }, Validators.required],
            task: [{ value: '', disabled: true }, Validators.required],
            task_name: [{ value: '', disabled: true }, Validators.required],
            rowVersion: [''],
            type: [Type.Terminal],
            startDateTime: ['', Validators.required],
            endDateTime: [{ value: '', disabled: true }, Validators.required],
            // endDateTime: [{ value: '', disabled: true }, [this.dateTimeValidator().bind(this)]],     //  TODO [4ref] -- if operationForm can check TBA in correct way - this custom validator can be restored
            estimatedTimeHours: ['', Validators.required],
            estimatedTimeMinutes: ['', Validators.required],
            description: ['', Validators.required],
            notes: ['', [this.notesValidator()]],
            location: ['0', Validators.pattern('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$')],
            important: [''],
            responsible: ['', Validators.required],
            available: ['', [this.involvedPersonsValidator().bind(this)]],
            taskNumberSeries: [1000, Validators.required],
            taskDepartmentCode: [null, Validators.required],
            taskAssignmentDate: [null, Validators.required],
            taskReference: ['']
        });

    }

    // #region onInit
    ngOnInit(): void {
        // Mark important form fields in operationForm with internal marker (for enable them before submit operationForm)
        Object.keys(this.operationForm.controls).forEach((key) => {
            if (this.veryImportantFields.includes(key)) {
                (this.operationForm.get(key) as any).veryImportantValue = true;
            }
        });

        this.loginService.init();

        this.updateNewTaskValidators();

        this.subscriptions.push(
             this.departmentClient
                .getAllDepartments()
                .subscribe((response) => {
                    if (response?.departments) {

                        response.departments.forEach(d=> this.departmentCodes.push( ({
                            label: d.name!,
                            value: d.name!
                        }) ));
                    }
                    else {
                        throw new Error("Response from getAllDepartments() is invalid!");
                    }
                })

        )
        // get customers list from DB
        this.subscriptions.push(
            this.customerService.customersObservable.subscribe((cs) => {
                this.customers = cs;
                const trySetCustomer = () => {
                    const loginCustomer = this.loginService.currentCustomer?.customer;

                    if (!loginCustomer || !this.customers?.length) {
                        setTimeout(trySetCustomer, 50); // retry shortly
                        return;
                    }

                    const match = this.customers.find(c => c.id === loginCustomer.id);

                    const hasOperationCustomer =
                        this.operation?.task?.job?.customer?.id;

                    if (match && !this.selectedCustomer && !hasOperationCustomer) {
                        this.selectCustomer(match);
                    }

                };

                trySetCustomer();
            })
        );
        const request = new GetResourceRequest({
            type: "0"
        });

        // get workers list from DB
        this.subscriptions.push(
            this.resourceClient
                .getResourceList({
                    resourceGroups: [
                        ...this.responsible.groups,
                        ...this.available.groups.filter(
                            (group) =>
                                !this.responsible.groups.includes(group)
                        ),
                    ],
                    type: "0",
                } as GetResourceRequest)
                .subscribe((response) => {
                    if (response?.resources) {
                        this.updateFilteredWorkers(response);
                    }
                    else {
                        throw new Error("Response from getResourceList() is invalid!");
                    }
                })
        );
        // get Locations list from DB
        this.subscriptions.push(
            this.locationClient
                .getLocation()
                .subscribe((response) => {
                    if (response?.locations) {
                        this.updateLocations(response.locations);
                    }
                    else {
                        throw new Error("Response from getResourceList() is invalid!");
                    }
                })
        );
    };

    private updateNewTaskValidators(): void {
        const numberSeries = this.operationForm.get('taskNumberSeries');
        const department = this.operationForm.get('taskDepartmentCode');
        const assignmentDate = this.operationForm.get('taskAssignmentDate');

        if (!numberSeries || !department || !assignmentDate) return;

        if ((!this.isEditMode || this.updatingPendingOperation) && this.isNewTaskRequested) {
            numberSeries.setValidators([Validators.required]);
            department.setValidators([Validators.required]);
            assignmentDate.setValidators([Validators.required]);
        } else {
            numberSeries.clearValidators();
            department.clearValidators();
            assignmentDate.clearValidators();
        }

        numberSeries.updateValueAndValidity();
        department.updateValueAndValidity();
        assignmentDate.updateValueAndValidity();
    }

    // #region Customer FN
    private selectCustomer(customerObj?: Customer) {
        if (customerObj?.id && customerObj.name) {
            this.selectedCustomer = customerObj;
            this.operationForm.get("customer")?.setValue(customerObj, { emitEvent: false });
            this.updateFilteredJobs();
            return customerObj;
        }

        return undefined;
    }

    private selectCustomerById(customerId: number | string = 0) {
        if (!customerId || !this.customers) return;

        const customer = this.customers.find(c => c.id === customerId);
        if (customer) {
            this.selectCustomer(customer);
        }
    }

    private clearSelectedCustomer() {
        this.isNewJobRequested = false;
        this.searchByJobNumber = false;
        this.isNewTaskRequested = false;
        this.selectedCustomer = undefined;
        this.operationForm.get("customer")?.setValue(undefined);
        this.updateFilteredJobs();
    }

    get canChangeCustomer(): boolean {
        return this.loginService.hasRights(UserRight.ListCustomersTerminal);
    }

    searchCustomer = (text: Observable<string>) => {
        return text.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map((t) => t.toLowerCase().split(/\s+/)),
            map((t) =>
                this.customers
                    ? this.customers
                          .filter((c) =>
                              TerminalOperationCreateEditComponent.customerFilter(
                                  t,
                                  c
                              )
                          )
                          .slice(0, 10)
                    : []
            )
        );
    };

    private static customerFilter(
        lcTexts: string[],
        customer: Customer
    ): boolean {
        return (lcTexts.length === 1 && lcTexts[0].match(/^\d+$/))
            ? (!!customer.externalId && customer.externalId.indexOf(lcTexts[0]) >= 0)
            : lcTexts.every(
                (lcText) =>
                    customer.name.toLowerCase().indexOf(lcText) >= 0
                    || customer.address.toLowerCase().indexOf(lcText) >= 0
            );
    }

    customerAheadFormatter = (cust?: Customer) => {
        if (!cust) return "";
        const customerSearchInput = document.getElementById("customer") as HTMLInputElement;
        if (customerSearchInput) {
            if (customerSearchInput.value.match(/^\d+$/)) {
                return cust?.externalId ?? cust.name ?? "???";
            }
            else {
                return cust.address ? `${cust.name} (${cust.address})` : cust.name;
            }
        }
        return "";
    };
    customerValueFormatter = (cust?: Customer) => {
        if (cust instanceof Customer && cust?.name) {
            return cust.name;
        }
        return "";
    };

    onCustomerSelect($event: Event & { item?: Customer }) {
        this.searchByJobNumber = false;
        $event.preventDefault();
        if(!this.selectCustomer($event.item) && $event.item?.id) {
            this.selectCustomerById($event.item.id);
        }
    }

    checkSelectedCustomer($event: Event, focused: boolean = false) {
        this.isCustomerSearchFocused = focused;
        if (focused) { ($event?.target as HTMLInputElement).select(); }
        const operationFormCustomerValue = this.operationForm.get("customer")?.value;
        if (!operationFormCustomerValue?.id || this.selectedCustomer?.id !== operationFormCustomerValue.id) {
            if (this.customers) {
                const customerInput = $event?.target as HTMLInputElement;
                const inputValue = customerInput?.value.toLowerCase().trim() || '';
                const foundCustomer = this.customers.find(cust => cust.name.toLowerCase() === inputValue || (cust.externalId && cust.externalId.toLowerCase() === inputValue));
                if (foundCustomer) { this.selectCustomer(foundCustomer); }
                else { this.clearSelectedCustomer(); }
            }
            else { this.clearSelectedCustomer(); }
        }
    }
    // #endregion
    // #region Jobs FN
    searchJob = (text: Observable<string>) => {
        return text.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map((t) => t.toLowerCase().split(/\s+/)),
            map((t) =>
                this.filteredJobs
                    ? this.filteredJobs
                          .filter((j) =>
                              TerminalOperationCreateEditComponent.jobFilter(t, j)
                          )
                          .slice(0, 10)
                    : []
            )
        );
    };

    private static jobFilter(
        lcTexts: string[],
        job: ExtendedJob
    ): boolean {
        return (lcTexts.length === 1 && lcTexts[0].match(/^\d+$/))
            ? (!!job.externalId && job.externalId.indexOf(lcTexts[0]) >= 0)
            : lcTexts.every(
                (lcText) => job.name.toLowerCase().indexOf(lcText) >= 0
            );
    }

    jobAheadFormatter = (job?: ExtendedJob) => {
        if (!job) return "";
        if (job instanceof ExtendedJob) {
            const jobSearchInput = document.getElementById("job") as HTMLInputElement;
            if (jobSearchInput) {
                if (jobSearchInput.value.match(/^\d+$/)) {
                    return job?.externalId ?? job.name ?? "???";
                }
                else {
                    return job?.nonUniqName
                        ? `${job.name} (${job?.externalId ?? "- -"})`
                        : job.name ?? "???";
                }
            }
            return "";
        }
        return "";
    };

    jobValueFormatter = (job?: ExtendedJob) => {
        if (job instanceof ExtendedJob) {
            return job?.name ?? "???";
        }
        return "";
    };

    onJobSelect($event: Event & { item?: ExtendedJob }) {
        this.selectedJob = $event.item;
        $event.preventDefault();
        this.selectJobsByInstance(this.selectedJob);
    }

    jobSelectedFromDropDown($event: Event) {
        const selectEl = $event.target as HTMLSelectElement;
        selectEl.classList.remove("tabfocused");

        const selectedId = selectEl.value;
        this.selectedJob = this.filteredJobs.find(
            (job) => job.id === selectedId
        );

        this.selectJobsByInstance(this.selectedJob);
    }

    private selectJobsByInstance(job?: ExtendedJob) {
        if (job instanceof ExtendedJob && job?.id) {
            this.operationForm.get("job")?.setValue(this.selectedJob);
            const jobCustomer = this.customers?.find(cust => cust.id === job.customerId);
            if (jobCustomer && jobCustomer?.id !== this.selectedCustomer?.id) {
                this.selectCustomer(jobCustomer);
            }
            this.updateFilteredTasks();
        }
    }

    get canCreateJob(): boolean {
        return this.loginService.hasRights(UserRight.TerminalJobCreate);
    }

    private updateFilteredJobs() {
        if(this.searchByJobNumber) {
            this.toggleNewJobRequested(false);
            this.filteredJobs = this.fullJobsList;
        }
        else if (this.selectedCustomer?.id) {
            const job_selector = document.querySelector(
                "select#job_selector"
            ) as HTMLSelectElement;
            if (job_selector) {
                job_selector.selectedIndex = 0;
            }
            this.jobsFetched = false;
            this.filteredJobs = [];
            let subscription = this.jobClient
                .getJobList(null, this.selectedCustomer.id, null)
                .subscribe((response) => {
                    this.jobsFetched = true;
                    if (response.jobs.length) {
                        // extend response.jobs into filteredJobs
                        this.filteredJobs = response.jobs.map((job) => {
                            return Object.assign(
                                new ExtendedJob(), {
                                    ...job,
                                    nonUniqName: false, // it would be used in jobAheadFormatter() and job_selector option names
                                }
                            );
                        });
                        this.filteredJobs = this.checkListForUniqNames(this.filteredJobs);

                        if (this.selectedJob) {
                            if (this.selectedJob.customerId !== this.selectedCustomer?.id) {
                                this.selectedJob = this.filteredJobs?.find(
                                    (job) => job.id === this.selectedJob?.id
                                );
                            }
                            this.operationForm.get("job")?.setValue(
                                this.selectedJob?.id
                                    ? this.selectedJob
                                    : undefined
                            );
                            if (this.someKindOfOperationUpdate) {
                                if (this.selectedJob) {
                                    this.updateFilteredTasks();
                                }
                                else if (this.isEditMode && !this.updatingPendingOperation) {
                                    // TODO [4ref] -- add some ErrorLog or ErrorInform code here
                                    this.toastService.add(
                                        "ERROR: Cannot get any data for related Job!",
                                        "Please inform Lighthouse support team about this error",
                                        "warning"
                                    );
                                }
                            }
                        }
                        this.toggleNewJobRequested(false);
                    } else {
                        if (this.isEditMode && !this.updatingPendingOperation)  {
                            // TODO [4ref] -- add some ErrorLog or ErrorInform code here
                            this.toastService.add(
                                "ERROR: Cannot get any Jobs for this customer!",
                                "Please inform Lighthouse support team about this error",
                                "warning"
                            );
                        } else {
                            this.toastService.add(
                                "Jobs not found for this customer!",
                                "Enter job name to create new one",
                                "warning"
                            );
                        }
                        this.toggleNewJobRequested(true);
                        this.clearJobs();
                    }
                });
            this.subscriptions.push(subscription);
        } else {
            this.clearJobs();
        }
    }
    private clearJobs(): void {
        this.filteredJobs = [];
        this.selectedJob = undefined;
        this.operationForm.get("job")?.setValue(undefined);
        if(!this.isNewJobRequested) { this.toggleNewJobRequested(false); }
        this.updateFilteredTasks();
    }
    // #endregion


    // #region Tasks FN
    searchTask = (text: Observable<string>) => {
        return text.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map((t) => t.toLowerCase().split(/\s+/)),
            map((t) =>
                this.filteredTasks
                    ? this.filteredTasks
                          .filter((j) =>
                              TerminalOperationCreateEditComponent.taskFilter(t, j)
                          )
                          .slice(0, 10)
                    : []
            )
        );
    };

    private static taskFilter(
        lcTexts: string[],
        task: ExtendedTask
    ): boolean {
        return (lcTexts.length === 1 && lcTexts[0].match(/^\d+$/))
            ? (!!task.externalId && task.externalId.indexOf(lcTexts[0]) >= 0)
            : lcTexts.every(
                (lcText) => task.description.toLowerCase().indexOf(lcText) >= 0
            );
    }

    taskAheadFormatter = (task?: ExtendedTask) => {
        if (!task) return "";
        if (task instanceof ExtendedTask) {
            const taskSearchInput = document.getElementById("task") as HTMLInputElement;
            if (taskSearchInput) {
                if (taskSearchInput.value.match(/^\d+$/)) {
                    return task?.externalId ?? task.description ?? "???";
                }
                else {
                    return task?.nonUniqName
                        ? `${task.description} (${task?.externalId ?? "- -"})`
                        : task.description ?? "???";
                }
            }
            return "";
        }
        return "";
    };

    taskValueFormatter = (task?: ExtendedTask) => {
        if (task instanceof ExtendedTask) {
            return task?.description ?? "???";
        }
        return "";
    };

    onTaskSelect($event: Event & { item?: ExtendedTask }) {
        this.selectedTask = $event.item;
        $event.preventDefault();
        this.selectTasksByInstance(this.selectedTask);
    }

    taskSelectedFromDropDown($event: Event) {
        const selectEl = $event.target as HTMLSelectElement;
        selectEl.classList.remove("tabfocused");

        const selectedId = selectEl.value;
        this.selectedTask = this.filteredTasks.find(
            (task) => task.id === selectedId
        );

        this.selectTasksByInstance(this.selectedTask);
    }

    get canCreateTask(): boolean {
        return this.loginService.hasRights(UserRight.TerminalTaskCreate);
    }

    private selectTasksByInstance(task?: ExtendedTask) {
        if (task instanceof ExtendedTask && task?.id) {
            this.operationForm.get("task")?.setValue(this.selectedTask);
        }
    }

    private updateFilteredTasks() {
        if (this.selectedJob?.id) {
            const task_selector = document.querySelector(
                "select#task_selector"
            ) as HTMLSelectElement;
            if (task_selector) {
                task_selector.selectedIndex = 0;
            }
            this.tasksFetched = false;
            this.filteredTasks = [];
            let subscription = this.taskClient
                .getTaskList(null, this.selectedJob.id)
                .subscribe((response) => {
                    this.tasksFetched = true;
                    if (response.tasks.length) {
                        // extend response.tasks into filteredTasks
                        this.filteredTasks = response.tasks.map((task) => {
                            return Object.assign(
                                new ExtendedTask(), {
                                    ...task,
                                    name: task.description, // it would be used in checkListForUniqNames()
                                    nonUniqName: false, // it would be used in taskAheadFormatter() and task_selector option names
                                }
                            );
                        });
                        this.filteredTasks = this.checkListForUniqNames(this.filteredTasks);

                        if (this.selectedTask) {
                            if (this.selectedTask.jobId !== this.selectedJob?.id) {
                                this.selectedTask = this.filteredTasks?.find(
                                    (task) => task.id === this.selectedTask?.id
                                );
                            }
                            this.operationForm.get("task")?.setValue(
                                this.selectedTask?.id
                                    ? this.selectedTask
                                    : undefined
                            );
                            if (this.isEditMode && !this.updatingPendingOperation && !this.selectedTask) {
                                // TODO [4ref] -- add some ErrorLog or ErrorInform code here
                                this.toastService.add(
                                    "ERROR: Cannot get any data for related Task!",
                                    "Please inform Lighthouse support team about this error",
                                    "warning"
                                );
                            }
                        }
                        this.toggleNewTaskRequested(false);
                    } else {
                        if (this.isEditMode && !this.updatingPendingOperation) {
                            // TODO [4ref] -- add some ErrorLog or ErrorInform code here
                            this.toastService.add(
                                "ERROR: Cannot get any Tasks for this job!",
                                "Please inform Lighthouse support team about this error",
                                "warning"
                            );
                        } else {
                            this.toastService.add(
                                "Tasks not found for this job!",
                                "Enter task name to create new one",
                                "warning"
                            );
                        }
                        this.toggleNewTaskRequested(true);
                        this.clearTasks();
                    }
                });
            this.subscriptions.push(subscription);
        } else {
            this.clearTasks();
        }
    }
    private clearTasks(): void {
        this.filteredTasks = [];
        this.selectedTask = undefined;
        this.operationForm.get("task")?.setValue(undefined);
        if(!this.isNewTaskRequested) { this.toggleNewTaskRequested(false); }
    }
    // #endregion

    // #region Workers FN
    searchWorker = (text: Observable<string>) => {
        return text.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                // map((term) => term.trim()),
                // filter((term) => term.length >= 2),
                map((t) => t.toLowerCase().split(/\s+/)),
                map((t) =>
                    this.currentWorkersList?.workers
                        ? this.currentWorkersList?.workers
                            .filter((w) =>
                                TerminalOperationCreateEditComponent.workerFilter(t, w)
                            )
                            .slice(0, 10)
                        : []
                )
            );
    };

    private static workerFilter(
        lcTexts: string[],
        worker: ExtendedWorker
    ): boolean {
        return lcTexts.every(
            (lcText) => worker.name.toLowerCase().indexOf(lcText) >= 0
        );
    }

    workerValueFormatter = (worker?: ExtendedWorker) => {
        if (worker instanceof ExtendedWorker) {
            return worker?.nonUniqName
                ? `${worker.name} (${worker?.personNumber ?? "--"})`
                : worker?.name ?? "???";
        }
        return "";
    };

    onWorkerSelect($event: Event & { item?: ExtendedWorker }) {
        if (this.currentWorkersList?.category) {
            if ($event.item?.id) {  //  selected worker passed by $event.item
                $event.preventDefault();
                this.addWorkerById($event.item.id, this.currentWorkersList);
            }
        }
    }

    workerSelectedFromDropDown($event: Event, workersList: WorkersList) {
        const selectEl = $event.target as HTMLSelectElement;
        if (selectEl) {
            selectEl.classList.remove("tabfocused");
            if (workersList?.category) {
                this.addWorkerById(selectEl.value, workersList);
            }
            selectEl.selectedIndex = 0;
        }
    }

    private addWorkerById(personId: string, workersList: WorkersList) {
        if(personId) {
            const selectedWorker = workersList.workers.find(
                (worker) => worker.id === personId
            );
            if (selectedWorker) {
                if (workersList.category == 'responsible') {
                    this.operationForm.get(workersList.category)?.setValue(selectedWorker);
                }
                else if (workersList.category == 'available') {
                    this.addInvolved(selectedWorker);
                    this.operationForm.get(workersList.category)?.updateValueAndValidity();  // run custom validation for `available` formControl
                }
            }
            else {
                throw new Error(`Selected worker (${personId}) can't be found in ${workersList.category} persons list!`);
            }
        }
    }

    private addInvolved(person: ExtendedWorker) {
        if(person?.id) {
            this.involvedPersons.push(person);
            this.available.workers = this.available.workers.filter((i) => i.id !== person.id);
            this.operationForm.get(this.available.category)?.setValue("");
        }
    }

    public removeInvolved(person: ExtendedWorker) {
        if(person?.id) {
            this.available.workers.push(person);
            this.involvedPersons = this.involvedPersons.filter((i) => i.id !== person.id);

            // run custom validation for `available` formControl
            const availablePersonsFormControl = this.operationForm.get(this.available.category);
            if (availablePersonsFormControl) {
                availablePersonsFormControl.setValue("");
                availablePersonsFormControl.updateValueAndValidity();
            }

            // run background sorting for `#available_selector` HTML element
            if (!this.sortInProgres) {
                this.sortInProgres = true;
                this.sortAvailableWorkersRequest();
            }
            else { this.sortPending++; }
        }
    }

    private sortAvailableWorkersRequest() {
        this.sortAvailableWorkersProcess()
            .then(
                availableSorted => {
                    this.available.workers = availableSorted;}
            );
    }

    private sortAvailableWorkersProcess(): Promise<ExtendedWorker[]> {
        return new Promise(resolve => {
            setTimeout(
                () => {
                    this.sortPending = 0;
                    const result = [ ...this.available.workers].sort((a, b) => {
                        const nameA = a.nonUniqName ? `${a.name} (${a.personNumber})` : a.name;
                        const nameB = b.nonUniqName ? `${b.name} (${b.personNumber})` : b.name;
                        return nameA.localeCompare(nameB);
                    });
                    resolve(result);
                    this.sortInProgres = false;
                    setTimeout(
                        () => {
                            if (!this.sortInProgres && this.sortPending) {
                                this.sortAvailableWorkersRequest();
                            }
                        }
                        , 250
                    );
                }
                , 25
            );
        });
    }

    private updateFilteredWorkers(response: GetResourceListResponse) {
        const hasOnlyAcceptRight =
            this.loginService.hasRights(UserRight.TerminalOnlyOperationAccept);
        const userResourceId = this.loginService.currentUser?.resourceId ?? null;
        const shouldRestrict = hasOnlyAcceptRight && !!userResourceId;
        if (response?.resources && response.resources.length) {
            this.fetchedWorkers = [];
            this.responsible.workers = [];
            this.available.workers = [];
            response.resources?.forEach((worker) => {
                const isOwnWorker = worker.id === userResourceId;
                const isResponsible = worker.id === this.operation?.responsiblePerson?.id;
                const shouldIncludeWorker =
                    !shouldRestrict ||
                    isOwnWorker ||
                    isResponsible ||
                    this.fetchedWorkers.length === 0;

                if (shouldIncludeWorker) {
                    const listWorker = new ExtendedWorker({
                        id: worker?.id || "",
                        name: worker.name || ""
                    });

                    if (worker.externalId) {
                        listWorker.personNumber = worker.externalId;
                    }

                    this.fetchedWorkers.push(listWorker);
                    if (
                        this.responsible.groups.includes(worker.resourceGroupNo || "") ||
                        isOwnWorker
                    ) {
                        this.responsible.workers.push(listWorker);
                    }
                    if (
                        this.available.groups.includes(worker.resourceGroupNo || "") ||
                        isOwnWorker
                    ) {
                        this.available.workers.push(listWorker);
                    }
                }
            });
            this.responsible.workers = this.checkListForUniqNames(this.responsible.workers);
            this.available.workers = this.checkListForUniqNames(this.available.workers);
            if (this.someKindOfOperationUpdate) {
                if(this.operation?.responsiblePerson) {
                    this.addWorkerById(this.operation.responsiblePerson.id || `${this.operation.responsiblePerson}`, this.responsible);    // TODO [4ref] -- operationClient.getOperation() must return responsiblePerson as OperationListResource, not as string
                }
                if(this.pendingPersons.length) {
                    this.pendingPersons.forEach((wrkId)=>{ this.addWorkerById(wrkId, this.available); });
                }
            }
        }
        else {
            throw new Error("Given GetResourceListResponse is empty!");
        }
    }

    public involvedPersonsValidator(): ValidatorFn {
        return () => {
            return (this.involvedPersons.length > 0
                || this.isRequestMode
                || this.updatingPendingOperation
            )
                ? null
                : { itemsRequired: true };
        };
    }
    // #endregion

    // #region Create Job
    async CreateJob(): Promise<TerminalJob | false> {
        const jobname = this.operationForm.get("job_name")?.value || "Undefined Job Description";
        this.trigger.next(false);
        if (jobname && this.selectedCustomer?.id) {
            const code = this.operationForm.get('taskDepartmentCode')?.value;
            const department = this.departmentCodes.find(d => d.value === code);
            const job = new CreateJobRequest({
                customerId: this.selectedCustomer.id,
                name: jobname,
                responsibleDepartment: department?.label ?? '',
            });
            try {
                const response = await this.jobClient
                    .createJob(job)
                    .toPromise();
                if (response.job?.id) {
                    this.toastService.add(
                        "SUCCESS: New Job was created",
                        "Your job create request was processed successfully!",
                        "success"
                    );
                    return response.job;
                }
            } catch (error) {
                this.toastService.add(
                    "ERROR: New Job was not created",
                    "There is an error with job create request. Job can not be added!",
                    "warning"
                );
            } finally {
                this.trigger.next(true);
            }
        } else {
            this.toastService.add(
                "ERROR: Form data is not valid",
                "Please check Job and Customer fields for correct input",
                "warning"
            );
        }
        return false;
    }



    // #region Create Task
    async CreateTask(): Promise<TerminalTask | false> {
        const taskname = this.operationForm.get("task_name")?.value || "Undefined Task Description";
        this.trigger.next(false);
        if (taskname && this.selectedJob?.id) {
            const task = new CreateTaskRequest({
                id: Guid.newGuid(),
                jobId: this.selectedJob.id,
                taskStatus: "Pending" as any,
                shortDescription: "shortDescription string",
                description: taskname,
                responsibleWorker: this.operationForm.get("responsible")?.value.id,
                startDate: this.calculatedStartDateTime,
                endDate: this.calculatedEndDateTime,
                estimatedTime: "00:01",
                taskType: "Terminal" as any,
                locationId: this.operationForm.get("location")?.value,
                numberSeries: this.operationForm.get('taskNumberSeries')?.value,
                departmentCode: this.operationForm.get('taskDepartmentCode')?.value,
                assignmentDate: this.operationForm.get('taskAssignmentDate')?.value?.dateTime,
                taskReference: this.operationForm.get('taskReference')?.value
            });
            try {
                const response = await this.taskClient
                    .createTask(task)
                    .toPromise();
                if (response.task?.id) {
                    this.toastService.add(
                        "SUCCESS: New Task was created",
                        "Your task create request was processed successfully!",
                        "success"
                    );
                    return response.task;
                }
            } catch (error) {
                this.toastService.add(
                    "ERROR: New Task was not created",
                    "There is an error with task create request. Task can not be added!",
                    "warning"
                );
            } finally {
                this.trigger.next(true);
            }
        } else {
            this.toastService.add(
                "ERROR: Form data is not valid",
                "Please check Task and Job fields for correct input",
                "warning"
            );
        }
        return false;
    }

    // #region Create Operation
    async CreateOperation(): Promise<void> {
        if (
            this.dateTimeValidationFn() === null &&         // TODO [4ref] -- if operationForm can check TBA in correct way - this condition can be deleted
            this.operationForm.valid
        ) {
            const formData = this.operationForm.value;
/*
            this.calculatedStartDateTime = this.checkStartDateTime(formData.startDateTime.dateTime);
            this.calculatedEndDateTime = this.isLongTermOperation && formData.endDateTime?.dateTime
                ? formData.endDateTime.dateTime
                : this.getOperationEndTimeFromEstimatedTime(this.calculatedStartDateTime, formData.estimatedTimeHours, formData.estimatedTimeMinutes);
*/
            const startDateTime = formData.startDateTime as DateTimeTba;
            if(startDateTime.timeIsTba)
            {
                startDateTime.dateTime = new Date(startDateTime.dateTime.toDateString());//TimeIsTba, so reset it, so we can make an accurate estimated time later
            }
              /*  this.operationstartDateTimeBeforeUpdate
                && formData.startDateTime.dateTime.getTime() == this.operationstartDateTimeBeforeUpdate.getTime()
                    ? this.operationstartDateTimeBeforeUpdate
                    : this.checkStartDateTime(formData.startDateTime.dateTime);*/
            const endDateTime = this.isLongTermOperation && formData.endDateTime
                ? formData.endDateTime
                : this.getOperationEndTimeTbaFromEstimatedTime(startDateTime, formData.estimatedTimeHours, formData.estimatedTimeMinutes);


            if (this.isNewJobRequested) {
                const newJobInstance = await this.CreateJob();
                if (newJobInstance instanceof TerminalJob) {
                    this.selectedJob = newJobInstance as ExtendedJob;
                } else {
                    throw new Error("New job instance is not defined!");
                }
            }

            if (this.isNewTaskRequested) {
                const newTaskInstance = await this.CreateTask();
                if (newTaskInstance instanceof TerminalTask) {
                this.selectedTask = newTaskInstance as ExtendedTask;
                } else {
                    throw new Error("New task instance is not defined!");
                }
            }

            if (!this.selectedTask?.id) { throw new Error("New task instance is not defined!"); }

            let operation = new CreateOperationRequest({
                id: this.operation?.id || Guid.newGuid(),
                taskId: this.selectedTask.id,      // TODO [4upd] -- refactor with task externalId
                type: Type.Terminal,
                status: this.isRequestMode || this.updatingPendingOperation
                    ? Status.Pending
                    : this.loginService.hasRights(UserRight.TerminalOperationAccept)
                        ? Status.Active
                        : Status.Pending,
                description: formData.description,
                responsiblePerson: formData.responsible.id,
                involvedWorkers: this.involvedPersons.map((worker)=>{return worker.id;}),
                involvedMachines: [],
                startDateTime: startDateTime,//this.dateToDateTimeTba(this.calculatedStartDateTime),
                endDateTime: endDateTime,//this.dateToDateTimeTba(this.calculatedEndDateTime),
                locationId: formData.location,
                notes: formData.notes,
                important: formData.important,
            });

            this.trigger.next(false);
            this.isLoadingState = true;
            try {
                const result = await this.operationClient.createOperation(operation).toPromise();
                this.toastService.add(
                    "SUCCESS: New Operation was created",
                    "Your operation create request was processed successfully!",
                    "success"
                );
                this.activeModal.close(result);
            } catch (error) {
                this.toastService.add(
                    "ERROR: New Operation was not created",
                    "There is an error with operation create request. Operation can not be added!",
                    "warning"
                );
            } finally {
                this.isLoadingState = false;
                this.trigger.next(true);
            }
        } else {
            this.toastService.add(
                "WARNING: New Operation data is not valid",
                "Please check all required fields for correct input",
                "warning"
            );
            this.markFormGroupTouched();
        }
    }
    // #region Update Operation
    async UpdateOperation(): Promise<void> {
        if (
            this.dateTimeValidationFn() === null &&         // TODO [4ref] -- if operationForm can check TBA in correct way - this condition can be deleted
            this.operationForm.valid
        ) {
            const formData = this.getFormValues(this.operationForm);
            const startDateTime = formData.startDateTime as DateTimeTba;
            if(startDateTime.timeIsTba)
            {
                startDateTime.dateTime = new Date(startDateTime.dateTime.toDateString());//TimeIsTba, so reset it, so we can make an accurate estimated time later
            }
              /*  this.operationstartDateTimeBeforeUpdate
                && formData.startDateTime.dateTime.getTime() == this.operationstartDateTimeBeforeUpdate.getTime()
                    ? this.operationstartDateTimeBeforeUpdate
                    : this.checkStartDateTime(formData.startDateTime.dateTime);*/
            const endDateTime = this.isLongTermOperation && formData.endDateTime
                ? formData.endDateTime
                : this.getOperationEndTimeTbaFromEstimatedTime(startDateTime, formData.estimatedTimeHours, formData.estimatedTimeMinutes);

                if (this.isNewJobRequested) {
                const newJobInstance = await this.CreateJob();
                if (newJobInstance instanceof TerminalJob) {
                    this.selectedJob = newJobInstance as ExtendedJob;
                } else {
                    throw new Error("New job instance is not defined!");
                }
            }

            if (this.isNewTaskRequested) {
                const newTaskInstance = await this.CreateTask();
                if (newTaskInstance instanceof TerminalTask) {
                this.selectedTask = newTaskInstance as ExtendedTask;
                formData.task = this.selectedTask;
                } else {
                    throw new Error("New task instance is not defined!");
                }
            }

            if (!this.selectedTask?.id) { throw new Error("New task instance is not defined!"); }

            if (formData.job.id && formData.customer.id) {
                let operation = new TerminalOperation({
                    id: this.operation ? this.operation.id || Guid.newGuid() : Guid.newGuid(),
                    // jobId: formData.job.id,      // TODO [4upd] -- refactor with task externalId
                    taskId: formData.task.id,
                    // externalId: this.operation ? this.operation.task.externalId : undefined,      // TODO [4upd] -- refactor with task externalId
                    type: formData.type,
                    startDateTime: startDateTime,
                    endDateTime: endDateTime,
                    status: this.someKindOfOperationUpdate
                        ? this.operation?.status && this.updatingPendingOperation
                            ? this.operation.status
                            : Status.Active
                        : this.operation?.status || Status.Active,
                    description: formData.description,
                    responsiblePerson: formData.responsible.id,
                    involvedWorkers: this.involvedPersons.map((worker)=>{return worker.id;}),
                    involvedMachines: this.operation?.involvedMachines?.map((vehicle)=>{return vehicle.id ?? `${vehicle}`;}),
                    locationId: formData.location,
                    notes: formData.notes ?? '',
                    important: formData.important ?? '',
                });

                this.trigger.next(false);
                this.isLoadingState = true;
                try {
                    const result = await this.operationClient.updateOperation(operation.id, operation).toPromise();
                    this.toastService.add(
                        "SUCCESS: Operation was updated",
                        "Your operation update request was processed successfully!",
                        "success"
                    );
                    this.activeModal.close(result);
                } catch (error) {
                    this.toastService.add(
                        "ERROR: Operation was not updated",
                        "There is an error with operation update request. Operation data can not be saved!",
                        "warning"
                    );
                } finally {
                    this.isLoadingState = false;
                    this.trigger.next(true);
                }
            } else {
                this.toastService.add(
                    "WARNING: Job or Customer data is not ready",
                    "Please check that this information is loaded correctly",
                    "warning"
                );
                this.markFormGroupTouched(); // TODO [2check] -- check if disabled elements can be touched?
            }
        } else {
            this.toastService.add(
                "WARNING: Operation data is not valid",
                "Please check all required fields for correct input",
                "warning"
            );
            this.markFormGroupTouched();
        }
    }

    // #region Form FN
    private patchOperationFormValues(): void {
        if (this.operation) {
            if (!this.selectedCustomer) {
                this.selectedTask = new ExtendedTask();
                this.selectedTask = {
                    id: this.operation.task.id,
                    name: "",
                } as ExtendedTask;
            }
            if (!this.selectedCustomer) {
                this.selectedJob = new ExtendedJob();
                this.selectedJob = {
                    id: this.operation.task.job.id,
                    name: "",
                } as ExtendedJob;
                this.selectCustomerById(this.operation.task.job?.customer.id || 0);
            }
            if (this.isEditMode) {
                this.operationstartDateTimeBeforeUpdate = new Date(this.operation.startDateTime.dateTime);
            }
            let operationEstimatedTime = this.getOperationEstimatedTime(this.operation).split(":");
            if (parseInt(operationEstimatedTime[0]) > 0) {
                this.isLongTermOperation = true;
            }
            const estimatedTimeInMinutes = parseInt(operationEstimatedTime[2])
            if (estimatedTimeInMinutes > 0 && !this.periodsInMinutes.includes(estimatedTimeInMinutes)) {
                this.periodsInMinutes.push(estimatedTimeInMinutes);
                this.periodsInMinutes.sort();
            }

            if (this.operation.involvedWorkers?.length) {
                this.pendingPersons = [];
                this.operation.involvedWorkers.forEach(
                    (wrk)=>{
                        const workerId = wrk.id ?? `${wrk}`;    // TODO [4ref] -- operationClient.getOperation() must return involvedWorkers as OperationListResource[], not as string[]
                        const alreadyInvolvedWorker =  this.involvedPersons.find((worker) => worker.id === workerId);
                        if (!alreadyInvolvedWorker) {
                            if (this.available.workers.length) {
                                this.addWorkerById(workerId, this.available);
                            }
                            else { this.pendingPersons.push(workerId); }
                        }
                    }
                )
            }

            this.operationForm.reset({
                customer: {
                    value: this.selectedCustomer,
                    disabled: this.isEditMode && !this.updatingPendingOperation,
                },
                job: {
                    value:
                        this.selectedJob instanceof ExtendedJob
                            ? this.selectedJob
                            : "",
                    disabled: this.isEditMode && !this.updatingPendingOperation,
                },
                task: {
                    value:
                        this.selectedTask instanceof ExtendedTask
                            ? this.selectedTask
                            : "",
                    disabled: this.isEditMode && !this.updatingPendingOperation,
                },
                type: this.operation.type,
                startDateTime: this.operation.startDateTime, /*this.dateToDateTimeTba(
                    !(this.isCopyMode || this.isAcceptMode)
                        ? this.operation.startDateTime.dateTime
                        : this.checkStartDateTime(this.operation.startDateTime.dateTime, this.timeShiftInMinutes)
                ),
                */
                endDateTime: {
                    value: this.operation.endDateTime ? this.operation.endDateTime : undefined,
                    disabled: !this.isLongTermOperation,
                },
                estimatedTimeHours: parseInt(operationEstimatedTime[1]).toString(),
                estimatedTimeMinutes: parseInt(operationEstimatedTime[2]).toString(),
                description: this.operation.description,
                location: (this.operation.locationId && this.operation.locationId !== "00000000-0000-0000-0000-000000000000") ? this.operation.locationId : 0,
                notes: (this.operation as any).notes,
                important: (this.operation as any).important,
                responsible: this.responsible?.workers
                    .find(
                        (wrk) => wrk.id === (
                            this.operation
                                ? this.operation.responsiblePerson.id
                                    || `${this.operation.responsiblePerson}`    // TODO [4ref] -- operationClient.getOperation() must return responsiblePerson as OperationListResource, not as string
                                : ''
                        )
                    ),
            });
        }
    }

    private getFormValues(form: FormGroup): any {
        const reenabledControls: string[] = [];

        // enable important form controls to get their values
        Object.keys(form.controls).forEach((key) => {
            const control = form.get(key);
            if (control?.disabled && (control as any).veryImportantValue) {
                control.enable({ emitEvent: false });
                reenabledControls.push(key);
            }
        });

        const value = form.value;

        // restore 'disabled' property on form controls
        reenabledControls.forEach((key) => {
            form.get(key)?.disable({ emitEvent: false });
        });

        return value;
    }

    public checkFocusState(event: Event) {
        const selectedEl = event.target as HTMLInputElement;
        if (selectedEl) {
            switch (event.type) {
                case "click":
                    selectedEl.classList.remove("tabfocused");
                    selectedEl.classList.toggle("open");
                    break;
                case "focus":
                    selectedEl.classList.remove("tabfocused");
                    setTimeout(() => {
                        if (!selectedEl.classList.contains("open"))
                            selectedEl.classList.add("tabfocused");
                    }, 100);
                    break;
                case "blur":
                    selectedEl.classList.remove("tabfocused");
                    selectedEl.classList.remove("open");
                    break;

                default:
                    break;
            }
        }
    }

    private setFocusByElementId(elementId: string) {
        setTimeout(() => {
            document.getElementById(elementId)?.focus();
        }, 250);
        this.operationForm.get(elementId)?.markAsUntouched();
    }

    public activateSearchByJobNumber() {
        this.searchByJobNumber = true;
        if (this.selectedCustomer?.externalId !== this.defaultCustomerNumber.toString()) {
            const defaultCustomerNumber = this.customers?.find(cust => cust.externalId === this.defaultCustomerNumber.toString());
            this.selectCustomer(defaultCustomerNumber);
        }
        else { this.updateFilteredJobs(); }
        this.setFocusByElementId("job");
    }

    public toggleNewJobRequested(force: boolean | null = null) {
        if (!this.isEditMode || this.updatingPendingOperation) {
            this.isNewJobRequested = force ?? !this.isNewJobRequested;
            if (this.isNewJobRequested) {
                this.operationForm.get("job")?.disable({ emitEvent: false });
                this.operationForm.get("job_name")?.enable({ emitEvent: false });
                this.setFocusByElementId("job_name");
                this.toggleNewTaskRequested(true);
            } else {
                this.operationForm.get("job")?.enable({ emitEvent: false });
                this.operationForm.get("job_name")?.disable({ emitEvent: false });
                if (!(this.isCopyMode || this.isAcceptMode || this.updatingPendingOperation)) { this.setFocusByElementId("job"); }
                if (!this.selectedJob || this.filteredTasks.length) {
                    this.toggleNewTaskRequested(false);
                }
            }
        }
    }

    public toggleNewTaskRequested(force: boolean | null = null) {
        if (!this.isEditMode || this.updatingPendingOperation) {
            this.isNewTaskRequested = force ?? !this.isNewTaskRequested;
            this.updateNewTaskValidators();
            if (this.isNewTaskRequested) {
                this.operationForm.get("task")?.disable({ emitEvent: false });
                this.operationForm.get("task_name")?.enable({ emitEvent: false });
                if(!this.isNewJobRequested) { this.setFocusByElementId("task_name"); }
            } else {
                this.operationForm.get("task")?.enable({ emitEvent: false });
                this.operationForm.get("task_name")?.disable({ emitEvent: false });
                if(!this.isNewJobRequested) {
                    if (!(this.isCopyMode || this.isAcceptMode || this.updatingPendingOperation)) { this.setFocusByElementId("task"); }
                }
            }
        }
    }

    public notesValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const locationSelector = document.getElementById("location_selector") as HTMLSelectElement;
            if (!locationSelector) {return null;}
            // check if last option is selected
            const isLastSelected = locationSelector?.selectedIndex === (locationSelector?.options.length - 1);
            const isEmpty = !control.value?.trim();
            return locationSelector.value !== "0" && isLastSelected && isEmpty? { requiredIfLastLocationSelected: true } : null;
        };
    }

    public arrowKeyCheck(event: KeyboardEvent, typeaheadInstance: NgbTypeahead) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!typeaheadInstance.isPopupOpen()) {
                (event.target as HTMLElement).dispatchEvent(new Event('input'));
            }
        }
    }

    private markFormGroupTouched(): void {
        Object.keys(this.operationForm.controls).forEach((key) => {
            const control = this.operationForm.get(key);
            control?.markAsTouched();
        });
    }
    // #region DateTime FN

    private dateToDateTimeTba(date: Date, timeIsTba = false): DateTimeTba {
        const TbaDate = new DateTimeTba();
        TbaDate.dateTime = date;
        TbaDate.timeIsTba = timeIsTba;
        return TbaDate;
    }

    private checkNumRange(numValue: number | string = 0, maxNum: number | null = null): number {
        if (typeof numValue === 'string') numValue = parseInt(numValue.toString().trim());
        if (maxNum === null) { maxNum = Math.pow(2, 64); }
        if (isNaN(numValue) || numValue < 0) { numValue = 0; }
        return (numValue <= maxNum) ? numValue : maxNum;
    }

    restartDateTime(control_name: string): void {
        let readValue = this.operationForm.controls[control_name]?.value;
        if (readValue) {
            readValue.dateTime = this.checkStartDateTime(readValue.dateTime ?? undefined, control_name == 'endDateTime' ? 15 : 1);
        }
        this.operationForm.controls[control_name]?.setValue(readValue);
    }

    public dateTimeValidationFn() {
        const startDateTimeValue = this.operationForm.get('startDateTime')?.value?.dateTime.getTime();
        if (!isNaN(startDateTimeValue)) {
            const endDateTimeFormControl = this.operationForm.get('endDateTime');
            if(!endDateTimeFormControl?.disabled) {
                const endDateTimeValue = endDateTimeFormControl?.value.dateTime.getTime();
                if (!isNaN(endDateTimeValue)) {
                    if(startDateTimeValue < endDateTimeValue) {
                        return null;
                    }
                    else {
                        this.toastService.add(
                            "Incorrect Operation time range",
                            "Please set correct value for Start and End date / time.",
                            "warning"
                        );
                        return { incorrectDateTimeRange: true }
                    }
                }
                else {
                    this.toastService.add(
                        "Incorrect Operation End Time",
                        "Please set correct value for End date / time.",
                        "warning"
                    );
                }
                return { incorrectEndDateTime: true };
            }
            else {
                return null;
            }
        }
        else {
            this.toastService.add(
                "Incorrect Operation start Time",
                "Please set correct value for Start date / time.",
                "warning"
            );
        }
        return { incorrectStartDateTime: true };
    }

    public dateTimeValidator(): ValidatorFn {
        return this.dateTimeValidationFn;
    }

    public checkEstimatedTimeValues(formControlName = '', maxNum: number = 59): number {
        const estimatedTimeControl = this.operationForm.get(formControlName);
        let controlValue = 0;
        if (estimatedTimeControl) {
            controlValue = this.checkNumRange(estimatedTimeControl.value, maxNum);
            estimatedTimeControl.setValue(controlValue.toString(), { emitEvent: false });
        }
        return controlValue;
    }

    public onEstimatedTimeHoursChange(): void {
        const currHours = this.checkEstimatedTimeValues('estimatedTimeHours', 23);
        const estimatedTimeMinutesControl = this.operationForm.get('estimatedTimeMinutes');
        if (estimatedTimeMinutesControl?.value === '') {
            estimatedTimeMinutesControl.setValue(currHours > 0 ? '0' : this.timeShiftInMinutes.toString(), { emitEvent: false });
        }
        else if (currHours === 0 && estimatedTimeMinutesControl && parseInt(estimatedTimeMinutesControl.value) === 0) {
            estimatedTimeMinutesControl.setValue(this.timeShiftInMinutes.toString(), { emitEvent: false });
        }
    }

    public onEstimatedTimeMinutesChange(): void {
        const currMinutes = this.checkEstimatedTimeValues('estimatedTimeMinutes');
        const estimatedTimeHoursControl = this.operationForm.get('estimatedTimeHours');
        if (estimatedTimeHoursControl?.value === '') {
            estimatedTimeHoursControl.setValue(currMinutes > 0 ? '0' : '1', { emitEvent: false });
        }
        else if (currMinutes === 0 && estimatedTimeHoursControl && parseInt(estimatedTimeHoursControl.value) === 0) {
            this.operationForm.get('estimatedTimeMinutes')?.setValue(this.timeShiftInMinutes.toString(), { emitEvent: false });
        }
    }

    // #endregion

    // #region Template FN
    // TODO [4ref] - the same method as in parent DashboardComponent
    private getOperationEstimatedTime(operation: OperationListOperation): string {
        const start = new Date(operation.startDateTime?.dateTime).getTime();
        const end = new Date(operation.endDateTime?.dateTime || start).getTime();

        const diff_in_minutes = Math.floor((end - start) / 60000); // in minutes
        if(diff_in_minutes > 0) {
            const minutes_lasts = diff_in_minutes % 60;
            const diff_in_hours = Math.floor(diff_in_minutes / 60);
            const hours_lasts = diff_in_hours % 24;
            const diff_in_days = Math.floor(diff_in_hours / 24);
            // Reserve operation.estimatedTime for other components
            operation.estimatedTime = `${diff_in_days}:${hours_lasts}:${minutes_lasts}`;      // TODO [4upd] -- refactor - get rid of operation.estimatedTime  ON BACK-END!!!
        }
        else {operation.estimatedTime = '0:0:0';}      // TODO [4upd] -- refactor - get rid of operation.estimatedTime  ON BACK-END!!!
        return operation.estimatedTime;      // TODO [4upd] -- refactor - get rid of operation.estimatedTime  ON BACK-END!!!
    }

    private updateLocations(locationsList: TerminalLocation[] | undefined) {
        if(locationsList && locationsList.length) {
            this.locations = locationsList.sort((a, b) => {
                if (a.name === this.otherLocationName) return 1;
                if (b.name === this.otherLocationName) return -1;
                return 0;
            });
        }
        else {
            this.locations = [];
            this.toastService.add(
                "WARNING: Locations list is empty",
                "Please check that Locations are loaded correctly",
                "warning"
            )
        }
    }

    public onLocationChange() {
        const inputControl = this.operationForm.get("notes");
        if (!inputControl) { return; }
        inputControl.updateValueAndValidity();
        inputControl.markAsTouched();
    }


    public checkStartDateTime(startDateTime: Date | undefined = undefined, timeShiftInMinutes: number = 0): Date {
        const timeToCompare = new Date(this.now);
        if (timeShiftInMinutes > 0) {
            timeToCompare.setMinutes(timeToCompare.getMinutes() + timeShiftInMinutes);
        }
        if (startDateTime) {
            if (
                !isNaN(startDateTime.getTime())
                && startDateTime.getTime() > timeToCompare.getTime()
            ) {
                // if time part is 0 or not defined - set defined time
                if (startDateTime.getHours() === 0 && startDateTime.getMinutes() === 0) {
                    console.warn(">> Operation starting time can't be set as '00:00'! Instead, a default time was set.");
                    startDateTime.setHours(timeToCompare.getHours());
                    startDateTime.setMinutes(timeToCompare.getMinutes());
                }
            }
        }
        return startDateTime ?? timeToCompare;
    }

    private getOperationEndTimeTbaFromEstimatedTime(startDateTime: DateTimeTba, estimatedTimeHoursString: string, estimatedTimeMinutesString: string): DateTimeTba | undefined {
        const startDateTimeInMiliseconds = startDateTime.dateTime.getTime();

        const estimatedTimeHours = parseInt(estimatedTimeHoursString);
        const estimatedTimeMinutes = parseInt(estimatedTimeMinutesString);
        const estimatedTimeInMiliseconds = (
            (
                ((!isNaN(estimatedTimeHours) ? estimatedTimeHours : 0) * 60)
                + (!isNaN(estimatedTimeMinutes) ? estimatedTimeMinutes : 15)
            ) * 60000
        );
        return !isNaN(startDateTimeInMiliseconds)
            ? this.dateToDateTimeTba(new Date(
                startDateTimeInMiliseconds
                + estimatedTimeInMiliseconds
            ), startDateTime.timeIsTba)
            : undefined;
    }

    private getOperationEndTimeFromEstimatedTime(startDateTime: Date, estimatedTimeHoursString: string, estimatedTimeMinutesString: string): Date | undefined {
        const startDateTimeInMiliseconds = startDateTime.getTime();

        const estimatedTimeHours = parseInt(estimatedTimeHoursString);
        const estimatedTimeMinutes = parseInt(estimatedTimeMinutesString);
        const estimatedTimeInMiliseconds = (
            (
                ((!isNaN(estimatedTimeHours) ? estimatedTimeHours : 0) * 60)
                + (!isNaN(estimatedTimeMinutes) ? estimatedTimeMinutes : 15)
            ) * 60000
        );
        return !isNaN(startDateTimeInMiliseconds)
            ? new Date(
                startDateTimeInMiliseconds
                + estimatedTimeInMiliseconds
            )
            : undefined;
    }

    public toggleLongTermOperation() {
        this.isLongTermOperation = !this.isLongTermOperation;
        if(this.isLongTermOperation) {
            const endDateFormControl = this.operationForm.get('endDateTime');
            if (endDateFormControl) {
                const calculatedEndDate = this.getOperationEndTimeFromEstimatedTime(
                    this.operationForm.get('startDateTime')?.value.dateTime,
                    this.operationForm.get('estimatedTimeHours')?.value,
                    this.operationForm.get('estimatedTimeMinutes')?.value
                );
                if(calculatedEndDate) {
                    endDateFormControl.setValue(
                        this.dateToDateTimeTba(calculatedEndDate)
                    );
                }
                endDateFormControl.enable({ emitEvent: false });
                this.operationForm.get('estimatedTimeHours')?.disable({ emitEvent: false });
                this.operationForm.get('estimatedTimeMinutes')?.disable({ emitEvent: false });
            }
        }
        else {
            this.operationForm.get('endDateTime')?.disable({ emitEvent: false });
            this.operationForm.get('estimatedTimeHours')?.enable({ emitEvent: false });
            this.operationForm.get('estimatedTimeMinutes')?.enable({ emitEvent: false });
        }
    }

    public get someKindOfOperationCreate() {
        return this.isCreateMode || this.isRequestMode;
    }

    public get someKindOfOperationUpdate() {
        return this.isEditMode || this.isCopyMode || this.isAcceptMode;
    }

    public get updatingCompletedOperation() {
        return this.operation?.status === Status.Completed && (this.isEditMode || this.isCopyMode);
    }

    public get updatingPendingOperation() {
        return this.operation?.status === Status.Pending && (this.isEditMode || this.isCopyMode);
    }

    private checkListForUniqNames<T extends { name: string; nonUniqName: boolean }>(list: T[]): T[] {
        // validate against duplicated resource names
        const nameCount = list.reduce((acc, resource) => {
            if (resource?.name) {
                acc[resource.name] = (acc[resource.name] || 0) + 1; // count as many times resource.name exists in list.resources
            }
            return acc;
        }, {} as Record<string, number>);

        return list.map((listItem) => {
            listItem.nonUniqName = nameCount[listItem.name] > 1;
            return listItem;
        });
    }
    // #endregion

    // #region Modal
    private initModal(
        operationInstance: OperationListOperation | undefined,
        workingMode: ModeType | undefined = undefined,
        workingDate: Date | undefined,
        jobList: TerminalJob[] | undefined
    ): void {
        this.workingMode = workingMode ?? ModeType.Create;
        switch (workingMode) {
            case ModeType.Create:
                this.isCreateMode = true;
                break;
            case ModeType.Request:
                this.isRequestMode = true;
                break;
            case ModeType.Edit:
                this.isEditMode = true;
                break;
            case ModeType.Copy:
                this.isCopyMode = true;
                break;
            case ModeType.Accept:
                this.isAcceptMode = true;
                break;
            default:
                break;
        }
        this.operation = operationInstance ?? new OperationListOperation();
        if (operationInstance && operationInstance instanceof OperationListOperation) {
            this.patchOperationFormValues();
            this.subscriptions.push(
                this.operationClient
                    .getOperation(this.operation.id)
                    .subscribe((response) => {
                        this.operation = Object.assign(
                            // updates all values from TerminalOperation and represent it as OperationListOperation object
                            new OperationListOperation(),
                            this.operation,
                            response
                        );
                        if (this.isCopyMode) {
                            this.operation.id = Guid.newGuid();
                            // update a copying Operation's StartDateTime while keeping the original Operation's EstimatedTime
                          /*  if (!!this.operation.endDateTime) {
                                const operationEstimatedTimeInMiliseconds = this.operation.endDateTime.getTime() - this.operation.startDateTime.getTime();
                                this.operation.startDateTime = this.checkStartDateTime(this.operation.startDateTime, this.timeShiftInMinutes);
                                if (this.operation.startDateTime) {
                                    this.operation.endDateTime = new Date(this.operation.startDateTime.getTime() + operationEstimatedTimeInMiliseconds);
                                }
                            }
                                */
                        }
                        this.patchOperationFormValues();
                    })
            );
        }
        else {
            // initialization of the startDateTime value
            this.operationForm.get("startDateTime")?.setValue(
                this.dateToDateTimeTba(
                    this.checkStartDateTime(workingDate, this.timeShiftInMinutes),workingDate?.getMilliseconds()===0 ? true:false
                )
            );
        }
        if (jobList && jobList.length) {
            this.fullJobsList = jobList.map((job) => {
                return Object.assign(
                    new ExtendedJob(), {
                        ...job,
                        nonUniqName: false, // it would be used in jobAheadFormatter() and job_selector option names
                    }
                );
            });
            this.filteredJobs = this.checkListForUniqNames(this.filteredJobs);
        }
    }
    static open(
        modalService: NgbModal,
        operationInstance: OperationListOperation | undefined = undefined,
        workingMode: ModeType | undefined = undefined,
        workingDate: Date | undefined = undefined,
        jobList: TerminalJob[] | undefined = undefined
    ): TypedModalRef<TerminalOperationCreateEditComponent, CreateOperationRequest> {
        let modal = modalService.open(TerminalOperationCreateEditComponent, {
            size: "lg",
            windowClass: "full-height-modal",
            backdrop: 'static',
            keyboard: false
        });
        let modalComponent = <TerminalOperationCreateEditComponent>(
            modal.componentInstance
        );
        modalComponent.initModal(operationInstance, workingMode, new Date(workingDate ?? 0), jobList);
        return new TypedModalRef(modal);
    }
    public cancel() {
        this.activeModal.dismiss("user canceled");
    }

    public openLogbook(operationId: string): void {
        this.operation!.id = operationId;
        this.showLogbook = true;
    }

    public async toggleLogbook(): Promise<void> {

        this.showLogbook = !this.showLogbook;

        if (this.showLogbook) {

            await this.markCommentsAsRead();

            this.operation!.hasUnreadComments = false;
        }
    }

    public async markCommentsAsRead(): Promise<void> {

        if (!this.operation!.id) {
            return;
        }

        try {

            await this.operationCommentClient
                .markCommentsAsRead(this.operation!.id)
                .toPromise();

            this.hasUnreadComments = false;

        }
        catch (error) {

            console.error(error);

        }
    }

    // #endregion
}
